import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { useUserUUID } from 'utils/auth';
import { env } from '../../../src/constants';

const isStagingEnv = env.VITE_ENV !== 'production';

const loadBraze = async () => {
  return await import('@braze/web-sdk');
};

export type BrazeInstanceType = typeof import('@braze/web-sdk');
export type BrazeContextType = BrazeInstanceType | null | undefined;

export const BrazeContext = createContext<BrazeContextType>(undefined);

interface BrazeProviderProps {
  children: ReactNode;
}

export const BrazeProvider = ({ children }: BrazeProviderProps) => {
  const [brazeInstance, setBrazeInstance] = useState<BrazeContextType>(null);
  const uuid = useUserUUID();
  const { isLoading } = useAuth0();

  const { isEnabled: isPushEnabled, isLoading: isFeatureToggleLoading } =
    useFeatureToggle(FeatureFlag.PRO_TALENT_IS_PUSH_ENABLED);
  const { isEnabled: isInAppMessagesEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_IN_APP_MESSAGES_ENABLED
  );

  useEffect(() => {
    if (
      shouldInitializeBraze(
        isFeatureToggleLoading,
        isLoading,
        uuid,
        isPushEnabled
      )
    ) {
      const initializeBrazeClient = async () => {
        const braze = await loadBraze();

        braze.initialize(env.VITE_BRAZE_API_KEY, {
          baseUrl: env.VITE_BRAZE_ENDPOINT,
          manageServiceWorkerExternally: true,
          noCookies: true,
          allowUserSuppliedJavascript: true,
          enableLogging: isStagingEnv,
          ...(isStagingEnv ? { sessionTimeoutInSeconds: 10 } : {})
        });

        braze.changeUser(uuid as string);

        if (isInAppMessagesEnabled) braze.automaticallyShowInAppMessages();

        braze.openSession();

        setBrazeInstance(braze);
      };

      void initializeBrazeClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFeatureToggleLoading, uuid]);

  return (
    <BrazeContext.Provider value={brazeInstance}>
      {children}
    </BrazeContext.Provider>
  );
};

const shouldInitializeBraze = (
  isFeatureToggleLoading: boolean,
  isLoading: boolean,
  uuid: string | undefined,
  isPushEnabled: boolean
) => {
  return !isFeatureToggleLoading && !isLoading && uuid && isPushEnabled;
};
